<template>
  <form v-if="validationSchema" @submit.prevent="onSubmit">
    <slot name="hidden" />
    <template v-if="!currentSchema.spec?.meta?.noHeader">
      <div v-if="headerType == 'login'" class="bg-bgr-primary z-10 mb-5 flex items-center">
        <slot name="image" />
      </div>
      <ModalHeader v-else :title="stepNames[currentStepIdx] || name" @close="exit" />
    </template>
    <div class="form-content flex min-h-0 grow px-4">
      <slot>
        <FormStep v-for="(schema, idx) in validationSchema" :key="idx">
          <slot :name="`step-${stepKey}`" :schema="schema" :can-submit="canSubmit">
            <FormBuilderVeeStep :schema="schema">
              <template #header>
                <div v-if="hideTitle" class="invisible" />
                <slot :name="`step-${stepKey}-header`" />
              </template>

              <template v-for="field in schemaFields(schema)" #[`${field}`]>
                <slot :name="`step-${stepKey}-${field}`" :schema="schema" :field="field" />
              </template>

              <template v-for="field in schemaFields(schema)" #[`${field}-label`]>
                <slot :name="`step-${stepKey}-${field}-label`" :schema="schema" :field="field" />
              </template>
            </FormBuilderVeeStep>
          </slot>
        </FormStep>
      </slot>
    </div>
    <slot name="footer">
      <template v-if="!currentSchema.spec?.meta?.noFooter">
        <div v-if="footerType == 'create'" class="bg-bgr-primary relative z-10 flex justify-between px-6 py-4">
          <Button
            ref="prevButton"
            class="mr-auto w-full max-w-max"
            type="button"
            :class="hasPrevious ? 'opacity-100' : 'opacity-0'"
            :variant="ButtonVariant.Tertiary"
            @click="goToPrev"
            >{{ t("previous") }}</Button
          >
          <div class="ml-auto flex gap-2">
            <slot name="footer-buttons" />
            <Button
              v-if="hasContinueButton && canSubmit"
              type="submit"
              :variant="isLastStep && lastStepPositive ? ButtonVariant.Positive : ButtonVariant.Primary"
              :loading="loading"
            >
              {{ continueButtonText }}
            </Button>
          </div>
          <div
            v-if="showProgress && !currentSchema.spec.meta?.hideProgressBar"
            class="absolute right-0 left-0 -z-10 flex justify-center px-4 py-2.5"
          >
            <div v-for="(_, idx) in validationSchema" :key="idx">
              <div
                v-if="!_.spec.meta?.autoskip && !_.spec.meta?.hideInProgressBar"
                class="mt-2 h-0.5 transition-all duration-300"
                :class="[
                  idx <= currentStepIdx ? 'bg-bgr-inverse' : 'bg-bgr-tertiary',
                  idx == currentStepIdx ? 'w-10' : 'w-5',
                  { 'mx-1': idx == 0, 'mr-1': idx !== 0 },
                ]"
              />
            </div>
          </div>
        </div>
        <div v-if="footerType == 'default'" class="bg-bgr-primary z-10 flex p-4">
          <Button
            v-if="hasPrevious"
            ref="prevButton"
            class="w-full max-w-max"
            type="button"
            :variant="ButtonVariant.Tertiary"
            @click="goToPrev"
            >{{ t("previous") }}</Button
          >
          <div class="ml-auto flex gap-2">
            <Button v-if="canCancel" type="button" :variant="ButtonVariant.Tertiary" @click="exit">
              {{ cancelButtonText || t("cancel") }}
            </Button>
            <Button
              v-if="hasSkip"
              class="w-full max-w-max"
              type="button"
              :variant="ButtonVariant.Tertiary"
              @click="skip"
              >{{ t("skip_this_step") }}</Button
            >
            <Button
              v-if="hasContinueButton"
              class="w-full max-w-max"
              type="submit"
              :disabled="!canSubmit || hasSkip"
              :variant="isLastStep && lastStepPositive ? ButtonVariant.Positive : ButtonVariant.Primary"
              :loading="loading"
            >
              {{ continueButtonText }}
            </Button>
          </div>
        </div>
        <div v-if="footerType == 'login'" class="bg-bgr-primary z-10 flex flex-col gap-3">
          <Button
            v-if="hasContinueButton"
            class="mt-3 w-full"
            type="submit"
            :disabled="!canSubmit"
            :variant="isLastStep && lastStepPositive ? ButtonVariant.Positive : ButtonVariant.Primary"
            :loading="loading"
          >
            {{ continueButtonText }}
          </Button>
          <slot name="footer-buttons" />
        </div>
      </template>
    </slot>
  </form>
</template>

<script setup lang="ts">
  const { t } = useI18n();

  const props = withDefaults(
    defineProps<{
      canCancel?: boolean;
      cancelButtonText?: string;
      customFooter?: boolean;
      footerType?: "default" | "create" | "login";
      headerType?: "default" | "login";
      hideTitle?: boolean;
      lastStepPositive?: boolean;
      loading?: boolean;
      name?: string;
      showProgress?: boolean;
      splitFooter?: boolean;
      stepNames?: string[];
      submitButtonText?: string;
      validationSchema?: any[];
    }>(),
    {
      canCancel: false,
      cancelButtonText: "",
      customFooter: false,
      footerType: "default",
      headerType: "default",
      hideTitle: false,
      lastStepPositive: false,
      loading: false,
      name: "",
      showProgress: false,
      splitFooter: false,
      stepNames: () => [],
      submitButtonText: "",
      validationSchema: undefined,
    }
  );

  const emit = defineEmits(["submit", "exit", "skip", "next"]);

  const prevButton = useTemplateRef<HTMLButtonElement>("prevButton");
  const focusPrevButton = () => {
    if (prevButton.value) {
      prevButton.value.el.focus();
    }
  };

  const {
    canSubmit,
    continueButtonText,
    currentSchema,
    currentStepIdx,
    exit,
    form,
    goToLastStep,
    goToNext,
    goToPrev,
    hasContinueButton,
    hasPrevious,
    hasSkip,
    isLastStep,
    onSubmit,
    resetForm,
    schemaFields,
    setStep,
    setTouched,
    skip,
    stepKey,
  } = useFormBuilder({
    submitButtonText: props.submitButtonText,
    validationSchema: props.validationSchema,
    emit,
  });

  const { values, setValues, setFieldValue } = form;
  defineExpose({
    values,
    currentStepIdx,
    isLastStep,
    hasPrevious,
    canSubmit,
    stepKey,
    setValues,
    setFieldValue,
    goToLastStep,
    goToNext,
    goToPrev,
    resetForm,
    currentSchema,
    setTouched,
    setStep,
    onSubmit,
    focusPrevButton,
  });
</script>
